import { useState, useEffect } from 'react';
import {
  getUsers,
  approveUser,
  rejectUser,
  updateUser,
  getStudyAssociations,
  softDeleteUser,
  getDeletedUsers,
  reactivateUser,
  permanentDeleteUser,
  verifyUserEmail,
  notifyUserUpdate,
  getMembers,
  inviteUser,
  getInvitations,
  deleteInvitation,
  resendInvitation,
  type User,
  type DeletedUser,
  type StudyAssociation,
  type Member,
  type UserInvitation
} from '../lib/admin';
import { getUser } from '../lib/auth';
import { useToast, useConfirm } from './ui';
import { CheckCircle, XCircle, Edit, Mail, Calendar, Shield, Trash2, RotateCcw, CheckCheck, X, Building2, UserPlus, Send, Clock, RefreshCw } from 'lucide-react';

interface UserManagementProps {
  filterMemberId?: string | null;
  onClearFilter?: () => void;
}

export default function UserManagement({ filterMemberId, onClearFilter }: UserManagementProps) {
  const currentUser = getUser();
  const toast = useToast();
  const { confirm } = useConfirm();
  const [users, setUsers] = useState<User[]>([]);
  const [deletedUsers, setDeletedUsers] = useState<DeletedUser[]>([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [studyAssociations, setStudyAssociations] = useState<StudyAssociation[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editFullName, setEditFullName] = useState('');
  const [editStudyAssociationId, setEditStudyAssociationId] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editMemberId, setEditMemberId] = useState('');
  const [saving, setSaving] = useState(false);

  // Invitation state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showInvitations, setShowInvitations] = useState(false);
  const [invitations, setInvitations] = useState<UserInvitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteMemberId, setInviteMemberId] = useState('');
  const [inviteStudyAssociationId, setInviteStudyAssociationId] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteError, setInviteError] = useState('');

  useEffect(() => {
    loadData();
  }, [showDeleted]);

  useEffect(() => {
    if (showInvitations) {
      loadInvitations();
    }
  }, [showInvitations]);

  const loadInvitations = async () => {
    try {
      const invitationsData = await getInvitations();
      setInvitations(invitationsData);
    } catch (err: any) {
      console.error('Failed to load invitations:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      if (showDeleted && currentUser.role === 'systemadmin') {
        // Load deleted users
        const [deletedUsersData, associationsData, membersData] = await Promise.all([
          getDeletedUsers(currentUser.id),
          getStudyAssociations(),
          getMembers()
        ]);
        setDeletedUsers(deletedUsersData);
        setStudyAssociations(associationsData);
        setMembers(membersData);
      } else {
        // Load active users
        const [usersData, associationsData, membersData] = await Promise.all([
          getUsers(currentUser.id, true),
          getStudyAssociations(),
          getMembers()
        ]);
        setUsers(usersData);
        setStudyAssociations(associationsData);
        setMembers(membersData);
      }
    } catch (err: any) {
      setError(err.message || 'Kunne ikke laste brukere');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId: string) => {
    const confirmed = await confirm({
      title: 'Godkjenn bruker',
      message: 'Er du sikker på at du vil godkjenne denne brukeren?',
      confirmLabel: 'Godkjenn',
      variant: 'default'
    });
    if (!confirmed) return;

    try {
      await approveUser(currentUser.id, userId);
      toast.showSuccess('Bruker godkjent');
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved godkjenning: ${err.message}`);
    }
  };

  const handleReject = async (userId: string) => {
    const confirmed = await confirm({
      title: 'Avvis bruker',
      message: 'Er du sikker på at du vil avvise denne brukeren? Brukeren kan registrere seg på nytt senere.',
      confirmLabel: 'Avvis',
      variant: 'warning'
    });
    if (!confirmed) return;

    try {
      await rejectUser(currentUser.id, userId);
      toast.showSuccess('Bruker avvist');
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved avvisning: ${err.message}`);
    }
  };

  const handleDelete = async (user: User) => {
    // Only admin and systemadmin can delete users
    if (currentUser.role !== 'admin' && currentUser.role !== 'systemadmin') {
      toast.showWarning('Kun admin og systemadmin kan slette brukere');
      return;
    }

    // Prevent deleting yourself
    if (user.id === currentUser.id) {
      toast.showWarning('Du kan ikke slette din egen brukerkonto');
      return;
    }

    // Prevent deleting the primary systemadmin user
    if (user.id === 'a0000000-0000-0000-0000-000000000001') {
      toast.showWarning('Systemadministrator-brukeren kan ikke slettes');
      return;
    }

    const confirmed = await confirm({
      title: 'Deaktiver bruker',
      message: `Er du sikker på at du vil slette brukeren ${user.full_name}?\n\nBrukeren vil bli deaktivert og vil motta en e-postvarsling.`,
      confirmLabel: 'Deaktiver',
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await softDeleteUser(currentUser.id, user.id);
      toast.showSuccess(`Bruker ${user.full_name} er deaktivert. E-postvarsling er sendt.`);
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved sletting: ${err.message}`);
    }
  };

  const handleVerifyEmail = async (userId: string) => {
    if (currentUser.role !== 'systemadmin') {
      toast.showWarning('Kun systemadmin kan verifisere e-post manuelt');
      return;
    }

    const confirmed = await confirm({
      title: 'Verifiser e-post',
      message: 'Er du sikker på at du vil verifisere denne brukerens e-post manuelt?',
      confirmLabel: 'Verifiser',
      variant: 'default'
    });
    if (!confirmed) return;

    try {
      await verifyUserEmail(currentUser.id, userId);
      toast.showSuccess('E-post verifisert!');
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved verifisering: ${err.message}`);
    }
  };

  const handleReactivate = async (userId: string) => {
    if (currentUser.role !== 'systemadmin') {
      toast.showWarning('Kun systemadmin kan reaktivere brukere');
      return;
    }

    const confirmed = await confirm({
      title: 'Reaktiver bruker',
      message: 'Er du sikker på at du vil reaktivere denne brukeren?',
      confirmLabel: 'Reaktiver',
      variant: 'default'
    });
    if (!confirmed) return;

    try {
      await reactivateUser(currentUser.id, userId);
      toast.showSuccess('Bruker reaktivert!');
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved reaktivering: ${err.message}`);
    }
  };

  const handlePermanentDelete = async (userId: string, userFullName: string) => {
    if (currentUser.role !== 'systemadmin') {
      toast.showWarning('Kun systemadmin kan slette brukere permanent');
      return;
    }

    const confirmed = await confirm({
      title: 'Permanent sletting',
      message: `ADVARSEL: Er du helt sikker på at du vil slette ${userFullName} PERMANENT?\n\nAlle data inkludert studieplaner vil bli slettet fra databasen og kan IKKE gjenopprettes.`,
      confirmLabel: 'Slett permanent',
      variant: 'danger',
      requireInput: 'SLETT',
      inputPlaceholder: 'Skriv SLETT for å bekrefte'
    });
    if (!confirmed) return;

    try {
      await permanentDeleteUser(currentUser.id, userId);
      toast.showSuccess(`Bruker ${userFullName} er permanent slettet.`);
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved permanent sletting: ${err.message}`);
    }
  };

  // Invitation handlers
  const handleOpenInviteModal = () => {
    setInviteEmail('');
    setInviteRole('member');
    setInviteMemberId('');
    setInviteStudyAssociationId(currentUser.study_association_id || '');
    setInviteError('');
    setShowInviteModal(true);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
    document.body.style.overflow = 'unset';
  };

  const handleSendInvite = async () => {
    if (!inviteEmail) {
      setInviteError('E-postadresse er påkrevd');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      setInviteError('Ugyldig e-postadresse');
      return;
    }

    try {
      setInviteSending(true);
      setInviteError('');

      const result = await inviteUser({
        email: inviteEmail,
        study_association_id: currentUser.role === 'systemadmin' ? inviteStudyAssociationId : undefined,
        member_id: (inviteRole === 'member' || inviteRole === 'sensor') && inviteMemberId ? inviteMemberId : null,
        role: inviteRole,
      });

      if (!result.success) {
        setInviteError(result.error || 'Kunne ikke sende invitasjon');
        return;
      }

      toast.showSuccess(`Invitasjon sendt til ${inviteEmail}`);
      handleCloseInviteModal();
      if (showInvitations) {
        loadInvitations();
      }
    } catch (err: any) {
      setInviteError(err.message || 'Kunne ikke sende invitasjon');
    } finally {
      setInviteSending(false);
    }
  };

  const handleDeleteInvitation = async (invitationId: string) => {
    const confirmed = await confirm({
      title: 'Slett invitasjon',
      message: 'Er du sikker på at du vil slette denne invitasjonen?',
      confirmLabel: 'Slett',
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await deleteInvitation(invitationId);
      toast.showSuccess('Invitasjon slettet');
      loadInvitations();
    } catch (err: any) {
      toast.showError(`Feil: ${err.message}`);
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      await resendInvitation(invitationId);
      toast.showSuccess('Invitasjon sendt på nytt');
      loadInvitations();
    } catch (err: any) {
      toast.showError(`Feil: ${err.message}`);
    }
  };

  const handleEdit = (user: User) => {
    // Prevent editing the primary systemadmin user
    if (user.id === 'a0000000-0000-0000-0000-000000000001') {
      toast.showWarning('Systemadministrator-brukeren kan ikke redigeres');
      return;
    }

    setEditingUser(user);
    setEditEmail(user.email);
    setEditFullName(user.full_name);
    setEditStudyAssociationId(user.study_association_id);
    setEditRole(user.role);
    setEditMemberId(user.member_id || '');

    // Scroll to top and lock body scroll when modal opens
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.body.style.overflow = 'hidden';
  };

  const handleCloseModal = () => {
    setEditingUser(null);
    document.body.style.overflow = 'unset';
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;

    // Prevent editing the primary systemadmin user
    if (editingUser.id === 'a0000000-0000-0000-0000-000000000001') {
      toast.showWarning('Systemadministrator-brukeren kan ikke redigeres');
      handleCloseModal();
      return;
    }

    try {
      setSaving(true);

      // Track changed fields for email notification
      const updatedFields: Record<string, string> = {};

      if (editEmail !== editingUser.email) {
        updatedFields.email = editEmail;
      }
      if (editFullName !== editingUser.full_name) {
        updatedFields.full_name = editFullName;
      }
      if (editStudyAssociationId !== editingUser.study_association_id) {
        const newAssociation = studyAssociations.find(a => a.id === editStudyAssociationId);
        updatedFields.study_association_id = newAssociation?.name || editStudyAssociationId;
      }
      if (editRole !== editingUser.role) {
        const roleNames: Record<string, string> = {
          'admin': 'Administrator',
          'member': 'Medlem',
          'sensor': 'Sensor',
          'systemadmin': 'Systemadministrator'
        };
        updatedFields.role = roleNames[editRole] || editRole;
      }
      if (editMemberId !== (editingUser.member_id || '')) {
        const newMember = members.find(m => m.id === editMemberId);
        updatedFields.member_id = newMember?.organization_name || 'Ingen';
      }

      await updateUser(currentUser.id, editingUser.id, {
        email: editEmail !== editingUser.email ? editEmail : undefined,
        full_name: editFullName !== editingUser.full_name ? editFullName : undefined,
        study_association_id: editStudyAssociationId !== editingUser.study_association_id ? editStudyAssociationId : undefined,
        role: editRole !== editingUser.role ? editRole : undefined,
        member_id: editMemberId !== (editingUser.member_id || '') ? (editMemberId || null) : undefined,
      });

      // Send email notification if any fields changed
      if (Object.keys(updatedFields).length > 0) {
        try {
          await notifyUserUpdate(
            editEmail, // Use new email if changed
            editFullName, // Use new name if changed
            updatedFields
          );
        } catch (emailErr: any) {
          console.error('Failed to send update notification:', emailErr);
          // Don't fail the update if email fails
        }
      }

      handleCloseModal();
      toast.showSuccess('Bruker oppdatert');
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved lagring: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending_approval':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-kf-accent">
            Venter godkjenning
          </span>
        );
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Aktiv
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Avvist
          </span>
        );
      default:
        return null;
    }
  };

  // Apply member filter if set
  const filteredUsers = filterMemberId
    ? users.filter(u => u.member_id === filterMemberId)
    : users;

  const pendingUsers = filteredUsers.filter(u => u.status === 'pending_approval');
  const activeUsers = filteredUsers.filter(u => u.status === 'active');

  // Get the member name for display
  const filterMemberName = filterMemberId
    ? members.find(m => m.id === filterMemberId)?.organization_name
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kf-primary"></div>
      </div>
    );
  }

  if (!currentUser || currentUser.role === 'member') {
    return (
      <div className="card text-center py-12">
        <Shield className="mx-auto mb-4 text-gray-400" size={64} />
        <h2 className="text-xl font-bold mb-2">Ingen tilgang</h2>
        <p className="text-gray-600">Du har ikke tilgang til brukeradministrasjon.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter banner */}
      {filterMemberId && filterMemberName && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="text-blue-600" size={20} />
              <div>
                <p className="font-medium text-blue-800">
                  Viser brukere for: {filterMemberName}
                </p>
                <p className="text-sm text-blue-600">
                  {filteredUsers.length} bruker{filteredUsers.length !== 1 ? 'e' : ''} tilknyttet denne organisasjonen
                </p>
              </div>
            </div>
            <button
              onClick={onClearFilter}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200"
            >
              <X size={16} />
              Vis alle
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Brukeradministrasjon</h1>
          <p className="mt-1 text-sm text-gray-700">
            Administrer brukere for {currentUser.role === 'systemadmin' ? 'alle studieforbund' : 'ditt studieforbund'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleOpenInviteModal}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-kf-primary hover:bg-kf-secondary"
          >
            <UserPlus size={16} className="sm:mr-2" />
            <span className="hidden sm:inline">Inviter bruker</span>
          </button>
          <button
            onClick={() => setShowInvitations(!showInvitations)}
            className={`inline-flex items-center px-3 py-2 border text-sm font-medium rounded-md ${
              showInvitations
                ? 'border-kf-primary text-kf-primary bg-kf-primary-10'
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
            }`}
            title={showInvitations ? 'Skjul invitasjoner' : 'Vis invitasjoner'}
          >
            <Send size={16} className="sm:mr-2" />
            <span className="hidden sm:inline">{showInvitations ? 'Skjul' : 'Invitasjoner'}</span>
          </button>
          {currentUser.role === 'systemadmin' && (
            <button
              onClick={() => setShowDeleted(!showDeleted)}
              className={`inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md ${
                showDeleted
                  ? 'text-white bg-red-600 hover:bg-red-700'
                  : 'text-white bg-gray-600 hover:bg-gray-700'
              }`}
              title={showDeleted ? 'Vis aktive brukere' : 'Vis slettede brukere'}
            >
              <Trash2 size={16} className="sm:mr-2" />
              <span className="hidden sm:inline">{showDeleted ? 'Aktive' : 'Slettede'}</span>
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Invitations Section */}
      {showInvitations && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6">
          <h2 className="text-lg font-medium text-blue-900 mb-4">
            Sendte invitasjoner ({invitations.length})
          </h2>
          {invitations.length === 0 ? (
            <p className="text-sm text-gray-600">Ingen invitasjoner sendt.</p>
          ) : (
            <div className="space-y-3">
              {invitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="bg-white rounded-lg border border-blue-200 p-3 sm:p-4"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm sm:text-base font-medium text-gray-900 break-all">{invitation.email}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          invitation.status === 'accepted'
                            ? 'bg-green-100 text-green-800'
                            : invitation.status === 'expired'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {invitation.status === 'accepted' ? 'Akseptert' : invitation.status === 'expired' ? 'Utløpt' : 'Venter'}
                        </span>
                      </div>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs sm:text-sm text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <Shield size={12} className="flex-shrink-0" />
                          <span>{invitation.role === 'admin' ? 'Admin' : invitation.role === 'sensor' ? 'Sensor' : 'Medlem'}</span>
                        </div>
                        {currentUser.role === 'systemadmin' && (
                          <div className="flex items-center gap-1.5">
                            <Building2 size={12} className="flex-shrink-0" />
                            <span className="truncate">{invitation.study_association_name}</span>
                          </div>
                        )}
                        {invitation.member_name && (
                          <div className="flex items-center gap-1.5">
                            <Building2 size={12} className="flex-shrink-0" />
                            <span className="truncate">{invitation.member_name}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5">
                          <Clock size={12} className="flex-shrink-0" />
                          <span>Utløper: {new Date(invitation.expires_at).toLocaleDateString('no-NO')}</span>
                        </div>
                      </div>
                    </div>
                    {invitation.status === 'pending' && (
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleResendInvitation(invitation.id)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                          title="Send på nytt"
                        >
                          <RefreshCw size={14} className="sm:mr-1" />
                          <span className="hidden sm:inline">Send på nytt</span>
                        </button>
                        <button
                          onClick={() => handleDeleteInvitation(invitation.id)}
                          className="inline-flex items-center p-1.5 sm:px-2.5 sm:py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                          title="Slett invitasjon"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Deleted Users Section */}
      {showDeleted && currentUser.role === 'systemadmin' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-medium text-red-900 mb-4">
            Slettede brukere ({deletedUsers.length})
          </h2>
          {deletedUsers.length === 0 ? (
            <p className="text-sm text-gray-600">Ingen slettede brukere funnet.</p>
          ) : (
            <div className="space-y-4">
              {deletedUsers.map((user) => (
                <div
                  key={user.id}
                  className="bg-white rounded-lg border border-red-300 p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-medium text-gray-900">{user.full_name}</h3>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Slettet
                        </span>
                      </div>
                      <div className="mt-2 space-y-1 text-sm text-gray-600">
                        <div className="flex items-center space-x-2">
                          <Mail size={16} />
                          <span>{user.email}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Shield size={16} />
                          <span>{user.study_association_name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Calendar size={16} />
                          <span>Slettet: {new Date(user.deleted_at).toLocaleDateString('no-NO')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => handleReactivate(user.id)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                        title="Reaktiver bruker"
                      >
                        <RotateCcw size={16} className="mr-1" />
                        Reaktiver
                      </button>
                      <button
                        onClick={() => handlePermanentDelete(user.id, user.full_name)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                        title="Slett permanent"
                      >
                        <Trash2 size={16} className="mr-1" />
                        Slett permanent
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pending Users Section */}
      {!showDeleted && pendingUsers.length > 0 && (
        <div className="bg-kf-accent-20 border border-kf-accent-40 rounded-lg p-6">
          <h2 className="text-lg font-medium text-kf-accent mb-4">
            Ventende brukere ({pendingUsers.length})
          </h2>
          <div className="space-y-4">
            {pendingUsers.map((user) => (
              <div
                key={user.id}
                className="bg-white rounded-lg border border-kf-accent-60 p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-lg font-medium text-gray-900">{user.full_name}</h3>
                      {getStatusBadge(user.status)}
                    </div>
                    <div className="mt-2 space-y-1 text-sm text-gray-600">
                      <div className="flex items-center space-x-2">
                        <Mail size={16} />
                        <span>{user.email}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Shield size={16} />
                        <span>{user.study_association_name}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Calendar size={16} />
                        <span>Registrert: {new Date(user.created_at).toLocaleDateString('no-NO')}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    {currentUser.role === 'systemadmin' && !user.email_verified && (
                      <button
                        onClick={() => handleVerifyEmail(user.id)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-kf-primary hover:bg-kf-primary"
                        title="Verifiser e-post manuelt"
                      >
                        <CheckCheck size={16} className="mr-1" />
                        Verifiser e-post
                      </button>
                    )}
                    <button
                      onClick={() => handleApprove(user.id)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle size={16} className="mr-1" />
                      Godkjenn
                    </button>
                    <button
                      onClick={() => handleReject(user.id)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <XCircle size={16} className="mr-1" />
                      Avvis
                    </button>
                    {currentUser.role === 'admin' && user.id !== currentUser.id && (
                      <button
                        onClick={() => handleDelete(user)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                        title="Slett bruker"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Users Section */}
      {!showDeleted && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Aktive brukere ({activeUsers.length})
            </h2>

            {activeUsers.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">Ingen aktive brukere funnet</p>
            ) : (
              <>
                {/* Mobile Card Layout (< lg) */}
                <div className="lg:hidden space-y-3">
                  {activeUsers.map((user) => (
                    <div key={user.id} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-gray-900 truncate">{user.full_name}</h3>
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          {user.id !== 'a0000000-0000-0000-0000-000000000001' && (
                            <button
                              onClick={() => handleEdit(user)}
                              className="p-1.5 text-kf-primary hover:bg-kf-primary-10 rounded"
                              title="Rediger"
                            >
                              <Edit size={16} />
                            </button>
                          )}
                          {currentUser.role === 'admin' &&
                           user.id !== currentUser.id &&
                           user.id !== 'a0000000-0000-0000-0000-000000000001' && (
                            <button
                              onClick={() => handleDelete(user)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title="Slett"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-kf-primary-20 text-kf-primary">
                          {user.role === 'admin' ? 'Admin' : user.role === 'sensor' ? 'Sensor' : 'Medlem'}
                        </span>
                        {getStatusBadge(user.status)}
                      </div>
                      <div className="mt-2 text-xs text-gray-600 space-y-1">
                        {currentUser.role === 'systemadmin' && (
                          <p><span className="font-medium">Forbund:</span> {user.study_association_name}</p>
                        )}
                        {user.member_organization_name && (
                          <p><span className="font-medium">Org:</span> {user.member_organization_name}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop Table Layout (lg+) */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Bruker
                        </th>
                        {currentUser.role === 'systemadmin' && (
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Studieforbund
                          </th>
                        )}
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Organisasjon
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rolle
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="hidden xl:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Godkjent av
                        </th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Handlinger
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {activeUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-3 py-3">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]">{user.full_name}</div>
                              <div className="text-xs text-gray-500 truncate max-w-[200px]">{user.email}</div>
                            </div>
                          </td>
                          {currentUser.role === 'systemadmin' && (
                            <td className="px-3 py-3 text-sm text-gray-900 max-w-[150px]">
                              <span className="block truncate">{user.study_association_name}</span>
                            </td>
                          )}
                          <td className="px-3 py-3 text-sm text-gray-500 max-w-[180px]">
                            <span className="block truncate">{user.member_organization_name || '-'}</span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <span className="px-2 py-0.5 inline-flex text-xs font-medium rounded-full bg-kf-primary-20 text-kf-primary">
                              {user.role === 'admin' ? 'Admin' : user.role === 'sensor' ? 'Sensor' : user.role === 'systemadmin' ? 'Sysadmin' : 'Medlem'}
                            </span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            {getStatusBadge(user.status)}
                          </td>
                          <td className="hidden xl:table-cell px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                            {user.approved_by_name || '-'}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right">
                            <div className="flex justify-end gap-1">
                              {user.id !== 'a0000000-0000-0000-0000-000000000001' && (
                                <button
                                  onClick={() => handleEdit(user)}
                                  className="p-1.5 text-kf-primary hover:bg-kf-primary-10 rounded"
                                  title="Rediger bruker"
                                >
                                  <Edit size={16} />
                                </button>
                              )}
                              {currentUser.role === 'admin' &&
                               user.id !== currentUser.id &&
                               user.id !== 'a0000000-0000-0000-0000-000000000001' && (
                                <button
                                  onClick={() => handleDelete(user)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                  title="Slett bruker"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Rediger bruker
            </h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="editFullName" className="label">
                  Fullt navn
                </label>
                <input
                  id="editFullName"
                  type="text"
                  className="input"
                  value={editFullName}
                  onChange={(e) => setEditFullName(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="editEmail" className="label">
                  E-postadresse
                </label>
                <input
                  id="editEmail"
                  type="email"
                  className="input"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="editStudyAssociation" className="label">
                  Studieforbund
                </label>
                <select
                  id="editStudyAssociation"
                  className="input"
                  value={editStudyAssociationId}
                  onChange={(e) => {
                    setEditStudyAssociationId(e.target.value);
                    // Nullstill medlemsorganisasjon når studieforbund endres
                    if (e.target.value !== editingUser.study_association_id) {
                      setEditMemberId('');
                    }
                  }}
                >
                  {studyAssociations
                    .filter((association) => association.name !== 'Kompetanseforbundet')
                    .map((association) => (
                      <option key={association.id} value={association.id}>
                        {association.name}
                      </option>
                    ))}
                </select>
                {editStudyAssociationId !== editingUser.study_association_id && (
                  <p className="mt-1 text-sm text-kf-warning">
                    Advarsel: Endring av studieforbund vil sette brukeren til "venter godkjenning" i det nye studieforbundet.
                  </p>
                )}
              </div>
              {(editRole === 'member' || editRole === 'sensor') && (
                <div>
                  <label htmlFor="editMemberId" className="label">
                    Medlemsorganisasjon
                  </label>
                  <select
                    id="editMemberId"
                    className="input"
                    value={editMemberId}
                    onChange={(e) => setEditMemberId(e.target.value)}
                  >
                    <option value="">Velg organisasjon</option>
                    {members
                      .filter(m => {
                        // Filtrer medlemmer basert på VALGT studieforbund
                        return m.study_association_id === editStudyAssociationId;
                      })
                      .map(member => (
                        <option key={member.id} value={member.id}>
                          {member.organization_name}
                        </option>
                      ))
                    }
                  </select>
                  {editMemberId !== (editingUser.member_id || '') && (
                    <p className="mt-1 text-sm text-kf-primary">
                      Brukerens medlemsorganisasjon vil bli endret.
                    </p>
                  )}
                </div>
              )}
              {(currentUser.role === 'systemadmin' || currentUser.role === 'admin') && (
                <div>
                  <label htmlFor="editRole" className="label">
                    Rolle
                  </label>
                  <select
                    id="editRole"
                    className="input"
                    value={editRole}
                    onChange={(e) => {
                      setEditRole(e.target.value);
                      // Hvis rolle endres til admin eller systemadmin, fjern member_id
                      if (e.target.value === 'admin' || e.target.value === 'systemadmin') {
                        setEditMemberId('');
                      }
                    }}
                  >
                    <option value="member">Medlem</option>
                    <option value="admin">Administrator</option>
                    <option value="sensor">Sensor</option>
                    {currentUser.role === 'systemadmin' && (
                      <option value="systemadmin">Systemadministrator</option>
                    )}
                  </select>
                  {editRole !== editingUser.role && (
                    <p className="mt-1 text-sm text-kf-primary">
                      Brukerens rolle vil bli endret til {editRole === 'admin' ? 'Administrator' : editRole === 'sensor' ? 'Sensor' : 'Medlem'}.
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-between">
              <div>
                {(currentUser.role === 'systemadmin' ||
                  (currentUser.role === 'admin' && currentUser.study_association_id === editingUser.study_association_id)) &&
                  editingUser.id !== currentUser.id && (
                  <button
                    onClick={() => {
                      handleCloseModal();
                      handleDelete(editingUser);
                    }}
                    disabled={saving}
                    className="px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-50"
                  >
                    Deaktiver bruker
                  </button>
                )}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleCloseModal}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-kf-primary hover:bg-kf-secondary"
                >
                  {saving ? 'Lagrer...' : 'Lagre'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Inviter ny bruker
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Send en invitasjon via e-post. Mottakeren vil få en lenke for å opprette sin brukerkonto.
            </p>
            {inviteError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                {inviteError}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label htmlFor="inviteEmail" className="label">
                  E-postadresse *
                </label>
                <input
                  id="inviteEmail"
                  type="email"
                  className="input"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="bruker@example.com"
                />
              </div>

              {currentUser.role === 'systemadmin' && (
                <div>
                  <label htmlFor="inviteStudyAssociation" className="label">
                    Studieforbund
                  </label>
                  <select
                    id="inviteStudyAssociation"
                    className="input"
                    value={inviteStudyAssociationId}
                    onChange={(e) => {
                      setInviteStudyAssociationId(e.target.value);
                      setInviteMemberId('');
                    }}
                  >
                    {studyAssociations
                      .filter((association) => association.name !== 'Kompetanseforbundet')
                      .map((association) => (
                        <option key={association.id} value={association.id}>
                          {association.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              <div>
                <label htmlFor="inviteRole" className="label">
                  Rolle
                </label>
                <select
                  id="inviteRole"
                  className="input"
                  value={inviteRole}
                  onChange={(e) => {
                    setInviteRole(e.target.value);
                    if (e.target.value === 'admin') {
                      setInviteMemberId('');
                    }
                  }}
                >
                  <option value="member">Medlem</option>
                  <option value="sensor">Sensor</option>
                  {currentUser.role === 'systemadmin' && (
                    <option value="admin">Administrator</option>
                  )}
                </select>
              </div>

              {(inviteRole === 'member' || inviteRole === 'sensor') && (
                <div>
                  <label htmlFor="inviteMemberId" className="label">
                    Medlemsorganisasjon
                  </label>
                  <select
                    id="inviteMemberId"
                    className="input"
                    value={inviteMemberId}
                    onChange={(e) => setInviteMemberId(e.target.value)}
                  >
                    <option value="">Velg organisasjon (valgfritt)</option>
                    {members
                      .filter(m => m.study_association_id === (currentUser.role === 'systemadmin' ? inviteStudyAssociationId : currentUser.study_association_id))
                      .map(member => (
                        <option key={member.id} value={member.id}>
                          {member.organization_name}
                        </option>
                      ))
                    }
                  </select>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={handleCloseInviteModal}
                disabled={inviteSending}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Avbryt
              </button>
              <button
                onClick={handleSendInvite}
                disabled={inviteSending}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-kf-primary hover:bg-kf-secondary"
              >
                {inviteSending ? 'Sender...' : 'Send invitasjon'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
